import React from "react";
import { createColumnHelper } from "@tanstack/react-table";
import Tag from "ui-components/molecules/tag/tag";
import { cn } from "../../../services/common/className";
import { DispatchBoardQueueStatus } from "app/data/city-delivery/dispatch-board/models";
import moment from "moment";
import { ProgressRing } from "../../../ui-components/progress-ring/progressRing";
import NumberFormat from "react-number-format";
import XGSIcon from "ui-components/icon/xgsIcon";
import XGSIcons from "ui-components/icon/xgsIcons";

const columnHelper = createColumnHelper<any>();


const getIdentifier = (picklistNumber?: number, manifestNumber?: number) => {
  if (picklistNumber) return <div className="cell__dispatch-queue-identifier">PL-{picklistNumber}</div>;
  else if (manifestNumber) return <div className="cell__dispatch-queue-identifier">MF-{manifestNumber}</div>
  else return <div className="cell__dispatch-queue-identifier">--</div>
}

const getDoor = (door: string, status: DispatchBoardQueueStatus) => {
  if (!door && status === DispatchBoardQueueStatus.READY_TO_ASSIGN) return <div className="cell__dispatch-queue-door--assign">Assign</div>;
  else return <div className="cell__dispatch-queue-door">{door}</div>;
}

const getStatus = (status: DispatchBoardQueueStatus) => {
  if (status === DispatchBoardQueueStatus.READY_TO_ASSIGN) return <Tag
    mix={cn("cell__dispatch-queue-status")("ready-to-assign")}
  >
    Ready to Assign
  </Tag>;
  else if (status === DispatchBoardQueueStatus.LOADING) return <Tag
    mix={cn("cell__dispatch-queue-status")("loading")}
  >
    Loading
  </Tag>;
  else if (status === DispatchBoardQueueStatus.READY_TO_CLOSE) return <Tag
    mix={cn("cell__dispatch-queue-status")("ready-to-close")}
  >
    Ready to Close
  </Tag>;
  else if (status === DispatchBoardQueueStatus.READY_TO_DISPATCH) return <Tag
    mix={cn("cell__dispatch-queue-status")("ready-to-dispatch")}
  >
    Ready to Dispatch
  </Tag>;
  else if (status === DispatchBoardQueueStatus.DISPATCHED) return <Tag
    mix={cn("cell__dispatch-queue-status")("dispatched")}
  >
    Dispatched
  </Tag>;
  else return status;
}

const getFormattedProgressMatric = (value?: number) => {
  if (!value) return "0";
  return <NumberFormat
    value={value}
    decimalScale={2}
    displayType={"text"}
    thousandSeparator={","}
    prefix={""}
  />
}

const getProgress = (
  status: DispatchBoardQueueStatus,
  probills: number,
  pieces: number,
  weight: number,
  loadedProbills: number,
  loadedPieces: number,
  loadedWeight: number,
  deliveredProbills: number,
  deliveredPieces: number,
  deliveredWeight: number
) => {
  let progressType = "Not Started";
  let toolTipText = "No process yet, please follow the workflow steps to process the load.";
  let progressIcon = XGSIcons.faHourglassStart;
  let progressValue = 0;
  let processedProbills = 0;
  let processedPieces = 0;
  let processedWeight = 0;

  if (status === DispatchBoardQueueStatus.LOADING || status === DispatchBoardQueueStatus.READY_TO_CLOSE) {
    progressType = "Loading";
    toolTipText = "Progress % = Pieces Loaded / Total Pieces";
    progressIcon = XGSIcons.faDolly;
    progressValue = (loadedPieces && pieces) ? Math.round((loadedPieces / pieces) * 100) : 0;
    processedProbills = loadedProbills;
    processedPieces = loadedPieces;
    processedWeight = loadedWeight;
  } else if (status === DispatchBoardQueueStatus.DISPATCHED) {
    progressType = "Delivery";
    toolTipText = "Progress % = Delivered Probills / Total Probills";
    progressIcon = XGSIcons.faTruck;
    progressValue = (deliveredProbills && probills) ? Math.round((deliveredProbills / probills) * 100) : 0;
    processedProbills = deliveredProbills;
    processedPieces = deliveredPieces;
    processedWeight = deliveredWeight;
  }

  return <div className="cell__dispatch-queue-progess">
    <ProgressRing
      value={progressValue}
      type={progressType}
      showLabel={true}
      legendDescription={toolTipText}
      icon={<XGSIcon icon={progressIcon}/>}
    />
    <div className="cell__dispatch-queue-progess__metrics">
      <div className="cell__dispatch-queue-progess__metrics__value">
        {getFormattedProgressMatric(processedProbills)} / {getFormattedProgressMatric(probills)} probills
      </div>
      <div className="cell__dispatch-queue-progess__metrics__value">
        {getFormattedProgressMatric(processedPieces)} / {getFormattedProgressMatric(pieces)} pieces
      </div>
      <div className="cell__dispatch-queue-progess__metrics__value">
        {getFormattedProgressMatric(processedWeight)} / {getFormattedProgressMatric(weight)} weight
      </div>
    </div>
  </div>
}

const getPlanDispatchTime = (planDispatchAt: string) => {
  if (!planDispatchAt) return "";
  const date = moment(planDispatchAt);
  return (
    <div>
      {date.format("MM/DD")}
      <span className="text-light">{date.format(" h:mm A")}</span>
    </div>
  )
}

const getDispatchTime = (dispatchedAt: string) => {
  if (!dispatchedAt) return "";
  const date = moment(dispatchedAt);
  return (
    <div>
      {date.format("MM/DD")}
      <span className="text-light">{date.format(" h:mm A")}</span>
    </div>
  )
}

const getEtaRange = (earliestEta?: string, latestEta?: string) => {
  if (!earliestEta && latestEta) return "";
  const earliestDate = (earliestEta) ? moment(earliestEta) : null;
  const latestDate = (latestEta) ? moment(latestEta) : null;
  return (
    <div className="cell__dispatch-queue-eta-range">
      {!!earliestDate && <span className="cell__dispatch-queue-eta-range__value">
        {earliestDate.format("MM/DD")}
      </span>
      }
      -
      {!!latestDate && <span className="cell__dispatch-queue-eta-range__value">
        {latestDate.format("MM/DD")}
      </span>
      }
    </div>
  )
}


export const getCityDeliveryDispatchBoardQueueColumns = () => [
  columnHelper.accessor("picklistNumber", {
    header: "Picklist / Manifest",
    id: "picklistNumber",
    size: 50,
    cell: (cellProps: any) => (
      <>
        {getIdentifier(cellProps.row.original.picklistNumber, cellProps.row.original.manifestNumber)}
      </>
    )
  }),
  columnHelper.accessor("door", {
    header: "Door",
    id: "door",
    size: 20,
    cell: (cellProps: any) => (
      <>
        {getDoor(cellProps.row.original.door, cellProps.row.original.status)}
      </>
    )
  }),
  columnHelper.accessor("status", {
    header: "Status",
    id: "status",
    size: 70,
    cell: (cellProps: any) => (
      <>
        {getStatus(cellProps.row.original.status)}
      </>
    )
  }),
  columnHelper.accessor("routeName", {
    header: "Route Name",
    id: "routeName",
    size: 50,
    cell: (cellProps: any) => (
      <>
        <div className="cell__dispatch-queue-route-name">{cellProps.row.original.routeName}</div>
      </>
    )
  }),
  columnHelper.accessor("stops", {
    header: "Stops",
    id: "stops",
    size: 20,
    cell: (cellProps: any) => (
      <>
        {cellProps.row.original.stops}
      </>
    )
  }),
  columnHelper.accessor("probills", {
    header: "Progress",
    id: "probills",
    size: 140,
    cell: (cellProps: any) => (
      <>
        {getProgress(
          cellProps.row.original.status,
          cellProps.row.original.probills,
          cellProps.row.original.pieces,
          cellProps.row.original.weight,
          cellProps.row.original.loadedProbills,
          cellProps.row.original.loadedPieces,
          cellProps.row.original.loadedWeight,
          cellProps.row.original.deliveredProbills,
          cellProps.row.original.deliveredPieces,
          cellProps.row.original.deliveredWeight
        )}
      </>
    )
  }),
  columnHelper.accessor("carrierNumber", {
    header: "Carrier",
    id: "carrierNumber",
    size: 50,
    cell: (cellProps: any) => (
      <>
        {cellProps.row.original.carrierName}
      </>
    )
  }),
  columnHelper.accessor("plannedDispatchedAt", {
    header: "Plan Dispatch Time",
    id: "plannedDispatchedAt",
    size: 50,
    cell: (cellProps: any) => (
      <>
        {getPlanDispatchTime(cellProps.row.original.plannedDispatchedAt)}
      </>
    )
  }),
  columnHelper.accessor("dispatchedAt", {
    header: "Dispatch Time",
    id: "dispatchedAt",
    size: 50,
    cell: (cellProps: any) => (
      <>
        {getDispatchTime(cellProps.row.original.dispatchedAt)}
      </>
    )
  }),
  columnHelper.accessor("earliestEta", {
    header: "ETA Range",
    id: "earliestEta",
    size: 60,
    cell: (cellProps: any) => (
      <>
        {getEtaRange(cellProps.row.original.earliestEta, cellProps.row.original.latestEta)}
      </>
    )
  }),
  columnHelper.accessor("driverNumber", {
    header: "Driver",
    id: "driverNumber",
    size: 50,
    cell: (cellProps: any) => (
      <>
        {cellProps.row.original.driverName}
      </>
    )
  })
];